import { createSlice } from "@reduxjs/toolkit";
import { ERROR } from "types";
import { CampaignCollaborationsSlice, SortOptions, CampaignCollaborationsFilters } from "types/campaign/collaborations";
import { fetchCampaignCollaborationsThunk, fetchCollaborationThunk } from "./thunk";

export const defaultCollaborationFilters: CampaignCollaborationsFilters = {
    page: 1,
    sort: SortOptions.MOST_RECENT,
    q: '',
};

export const defaultInsightsTableFilters: CampaignCollaborationsFilters = {
    page: 1,
    sort: SortOptions.MOST_VIEWS,
    has_approved_posts: 'y',
};

const initialState: CampaignCollaborationsSlice = {
    error: null,
    loading: false,
    campaigns: {},
    applied_filter_id: null,
    filters: defaultCollaborationFilters,
    tableFilters: defaultInsightsTableFilters,
};

const campaignCollaborationsSlice = createSlice({
    name: 'campaignCollaborations',
    initialState,
    reducers: {
        updateCollaborationFilters: (state, { payload }: { payload: CampaignCollaborationsFilters }) => {
            state.filters = payload;
        },
        updateCollaborationTableFilters: (state, { payload }: { payload: CampaignCollaborationsFilters }) => {
            state.tableFilters = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignCollaborationsThunk.pending, (state) => {
                state.error = null;
                state.loading = true;
            })
            .addCase(fetchCampaignCollaborationsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                const { filterId, campaignId, filters, collaborations } = action.payload;
                const existingData = state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList =
                    filters.page === 1 ? collaborations.items : [...existingData, ...collaborations.items];

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: collaborations.count > finalList.length,
                            filters,
                            count: collaborations.count,
                        },
                    },
                };
                state.applied_filter_id = filterId;
            })
            .addCase(fetchCampaignCollaborationsThunk.rejected, (state, { payload }) => {
                state.loading = false;
                if (payload !== ERROR.CACHED) {
                    state.error = payload || 'Failed to fetch collaborations!';
                }
            })
            .addCase(fetchCollaborationThunk.fulfilled, (state, { payload }) => {
                const { campaignId, collaborationId, collaboration } = payload;
                const existingData = state.campaigns[campaignId] ?? { filtered_lists: {} };
                state.campaigns[campaignId] = {
                    ...existingData,
                    collaborations: {
                        ...(existingData.collaborations ?? {}),
                        [collaborationId]: {
                            collaboration,
                            last_fetched_at: Date.now(),
                        },
                    },
                };
            });
    },
});

export const { updateCollaborationFilters, updateCollaborationTableFilters } = campaignCollaborationsSlice.actions;
export default campaignCollaborationsSlice.reducer;
